import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Dialog, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, Spinner, Text, makeStyles, tokens } from "@fluentui/react-components";
import { Chat16Regular } from "@fluentui/react-icons";
import { format } from "date-fns";
import { api } from "../lib/api.ts";

interface Msg {
  id: string;
  direction: "INBOUND" | "OUTBOUND";
  body: string;
  createdAt: string;
}

const useStyles = makeStyles({
  list: { display: "flex", flexDirection: "column", gap: "6px", maxHeight: "60vh", overflow: "auto", padding: "4px" },
  bubble: { maxWidth: "80%", padding: "6px 10px", borderRadius: "12px", whiteSpace: "pre-wrap", fontSize: tokens.fontSizeBase300 },
  out: { alignSelf: "flex-end", backgroundColor: tokens.colorBrandBackground2, borderBottomRightRadius: "2px" },
  in: { alignSelf: "flex-start", backgroundColor: tokens.colorNeutralBackground3, borderBottomLeftRadius: "2px" },
  time: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3, display: "block", marginTop: "2px" },
});

/** "Messages" button → dialog with the WhatsApp transcript for a client. */
export function MessageLog({ clientId, clientName }: { clientId: string; clientName: string }) {
  const s = useStyles();
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ["messages", clientId], queryFn: () => api.get<Msg[]>(`/api/clients/${clientId}/messages`), enabled: open });
  return (
    <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
      <DialogTrigger disableButtonEnhancement>
        <Button size="small" appearance="subtle" icon={<Chat16Regular />}>
          Messages
        </Button>
      </DialogTrigger>
      <DialogSurface style={{ maxWidth: 560 }}>
        <DialogBody>
          <DialogTitle>WhatsApp with {clientName}</DialogTitle>
          <DialogContent>
            {q.isLoading && <Spinner size="small" />}
            {q.data?.length === 0 && <Text>No messages yet.</Text>}
            <div className={s.list}>
              {(q.data ?? []).map((m) => (
                <div key={m.id} className={`${s.bubble} ${m.direction === "OUTBOUND" ? s.out : s.in}`}>
                  {m.body}
                  <span className={s.time}>
                    {m.direction === "OUTBOUND" ? "RackUp" : clientName} · {format(new Date(m.createdAt), "EEE d MMM HH:mm")}
                  </span>
                </div>
              ))}
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
